import { useLocation, useNavigate, useSearchParams } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import {
  IoInformationCircleOutline,
  IoPaperPlaneOutline,
} from "solid-icons/io";
import { createMemo, createSignal, onMount, Show } from "solid-js";
import { toast } from "solid-sonner";
import { useI18n } from "@/api/i18n";
import { CompanyLayoutMainContentContainer } from "@/components/layout/CompanyLayout";
import { Container } from "@/components/shared/Container";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loading } from "@/components/ui/loading";
import { MetaTitleWithSeparator } from "@/domains/business/seo";
import { useSendProposal } from "@/domains/company/active-search/api";
import {
  REDIRECT_SELECTION_SNAPSHOT_KEY,
  type SelectedFreelancer,
  useFreelanceSearchActions,
  useFreelanceSearchStore,
} from "@/domains/company/active-search/store/freelance-search-store";
import {
  buildPathWithQueryParams,
  filtersToFormDefaultValues,
  freelancerFilterFormToQueryParams,
} from "@/domains/company/active-search/utils/filterParams";
import { buildSendProposalRequest } from "@/domains/company/active-search/utils/selection";
import { createJobOfferQueryOptions } from "@/domains/job-offer/api";
import { FreelancePreviewCard } from "./components/FreelancePreviewCard";
import { ProposalPreview } from "./components/ProposalPreview";

export default function CompanySearchFreelancerSendProposalRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [storeState] = useFreelanceSearchStore();
  const actions = useFreelanceSearchActions();
  const sendProposalMutation = useSendProposal();
  const [searchParams] = useSearchParams();
  const [isGuardReady, setIsGuardReady] = createSignal(false);

  onMount(() => {
    queueMicrotask(() => {
      if (storeState.selectedFreelancers.length === 0) {
        const snapshot = sessionStorage.getItem(
          REDIRECT_SELECTION_SNAPSHOT_KEY,
        );
        if (snapshot) {
          try {
            const parsed = JSON.parse(snapshot) as SelectedFreelancer[];
            if (Array.isArray(parsed) && parsed.length > 0) {
              actions.setSelectedFreelancers(parsed);
            }
          } catch {
            // Ignore malformed snapshot and continue with current store state.
          } finally {
            sessionStorage.removeItem(REDIRECT_SELECTION_SNAPSHOT_KEY);
          }
        }
      }
      setIsGuardReady(true);
    });
  });

  const urlJobOfferId = createMemo(() => {
    const fromSearchParams = searchParams.job_offer_id;
    if (fromSearchParams) return String(fromSearchParams);

    const fromLocationQuery = location.query.job_offer_id;
    if (fromLocationQuery) return String(fromLocationQuery);

    return null;
  });

  // Sync job_offer_id from URL param to store (synchronous, before memos).
  // Also serves as a persistent fallback: if makePersisted's async hydration
  // races and resets the store, effectiveJobOfferId still reads the URL param.
  if (urlJobOfferId() && !storeState.jobOfferId) {
    actions.setJobOfferId(urlJobOfferId());
  }

  // Derive the effective ID from store (preferred) or URL param (fallback for
  // the async-hydration race where makePersisted can momentarily reset the store).
  const effectiveJobOfferId = () => storeState.jobOfferId ?? urlJobOfferId();

  const [dialogOpen, setDialogOpen] = createSignal(false);
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  const selectedFreelancers = () => storeState.selectedFreelancers;

  /** Deduplicate by id so recap and submit never show/send the same freelancer twice. */
  const uniqueSelectedFreelancers = createMemo(() => {
    const seen = new Set<string>();
    return selectedFreelancers().filter((f) => {
      const key = String(f.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  });

  const sendableFreelancers = createMemo(() =>
    uniqueSelectedFreelancers().filter((f) => !f.already_applied),
  );

  const jobOfferQuery = useQuery(() =>
    createJobOfferQueryOptions(effectiveJobOfferId() ?? ""),
  );

  const handleSubmit = async () => {
    const jobOfferId = effectiveJobOfferId();
    const toSend = sendableFreelancers();
    if (!jobOfferId || toSend.length === 0 || isSubmitting()) return;

    setIsSubmitting(true);

    const results = await Promise.allSettled(
      toSend.map((freelancer) =>
        sendProposalMutation.mutateAsync(
          buildSendProposalRequest(jobOfferId, freelancer),
        ),
      ),
    );

    setIsSubmitting(false);

    let successCount = 0;
    let failureCount = 0;

    results.forEach((result, index) => {
      const freelancer = toSend[index];
      const name = freelancer.full_name || `#${freelancer.id}`;

      if (result.status === "fulfilled") {
        if (result.value.success) {
          successCount++;
        } else {
          failureCount++;
          toast.error(`${name}: ${result.value.message}`);
        }
      } else {
        // The mutation already shows a generic toast via its onError handler.
        failureCount++;
      }
    });

    if (successCount > 0) {
      if (failureCount > 0) {
        toast.error(
          t(
            "company.search.freelancers.send_proposal.recap.partial_failure_toast",
            {
              count: failureCount,
            },
          ),
        );
      }
      setDialogOpen(true);
    }
  };

  const handleContinueResearch = () => {
    const filters = storeState.proposalPreFillFilters ?? {};
    const formValues = filtersToFormDefaultValues({
      ...filters,
      job_offer_id: storeState.jobOfferId ?? filters.job_offer_id,
    });
    const paramsObj = freelancerFilterFormToQueryParams(formValues);
    navigate(
      buildPathWithQueryParams(
        "/profile/company/search/freelancers/result",
        paramsObj,
      ),
    );
    sessionStorage.removeItem(REDIRECT_SELECTION_SNAPSHOT_KEY);
    queueMicrotask(() => {
      actions.clearFreelancers();
    });
  };

  const handleFinish = () => {
    setDialogOpen(false);
    toast.success(t("company.search.freelancers.send_proposal.success.title"));
    sessionStorage.removeItem(REDIRECT_SELECTION_SNAPSHOT_KEY);
    navigate(`/profile/company/ats/${effectiveJobOfferId()}?initiator=company`);
    actions.resetStore();
  };

  return (
    <>
      <MetaTitleWithSeparator>
        {t("company.search.freelancers.send_proposal.title")}
      </MetaTitleWithSeparator>
      <CompanyLayoutMainContentContainer>
        <Container size="full" class="py-6 space-y-5">
          <Show when={isGuardReady()} fallback={<Loading />}>
            <h1 class="text-3xl font-bold text-gray-900">
              {t("company.search.freelancers.send_proposal.title")}
            </h1>

            <Alert variant="info_outline" class="bg-blue-50/60">
              <IoInformationCircleOutline class="w-5 h-5" />
              <div>
                <AlertTitle>
                  {t(
                    "company.search.freelancers.send_proposal.preview.summary.title",
                  )}
                </AlertTitle>
                <AlertDescription>
                  {t(
                    "company.search.freelancers.send_proposal.preview.summary.description",
                  )}
                </AlertDescription>
              </div>
            </Alert>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div class="min-w-0">
                <Show when={jobOfferQuery.isSuccess} fallback={<Loading />}>
                  <Show
                    when={jobOfferQuery.data?.job_offer}
                    fallback={
                      <div class="rounded-lg border border-gray-200 bg-white p-6 text-gray-600">
                        {t("common.error")}
                      </div>
                    }
                  >
                    {(jobOffer) => <ProposalPreview job={jobOffer()} />}
                  </Show>
                </Show>
              </div>

              <div class="space-y-4 text-left min-w-0">
                <FreelancePreviewCard
                  freelancers={uniqueSelectedFreelancers()}
                />

                <Alert variant="info" class="bg-blue-50/60 border-blue-200">
                  <IoPaperPlaneOutline class="w-5 h-5" />
                  <div>
                    <AlertTitle>
                      {t(
                        "company.search.freelancers.send_proposal.preview.next_steps.title",
                      )}
                    </AlertTitle>
                    <AlertDescription>
                      <ul class="list-disc pl-4 space-y-1">
                        <li class="mt-2">
                          {t(
                            "company.search.freelancers.send_proposal.preview.next_steps.items.0",
                          )}
                        </li>

                        <li>
                          {t(
                            "company.search.freelancers.send_proposal.preview.next_steps.items.1",
                          )}
                        </li>
                      </ul>
                    </AlertDescription>
                  </div>
                </Alert>
              </div>
            </div>

            <div class="rounded-xl border bg-white p-4 text-left">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <p class="text-sm text-gray-600">
                  {t(
                    "company.search.freelancers.send_proposal.preview.footer_note",
                  )}
                </p>
                <div class="flex items-center justify-start gap-2">
                  <Button variant="outline" onClick={() => navigate(-1)}>
                    {t(
                      "company.search.freelancers.send_proposal.preview.cancel_button",
                    )}
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={
                      isSubmitting() || sendableFreelancers().length === 0
                    }
                  >
                    <Show
                      when={!isSubmitting()}
                      fallback={t(
                        "company.search.freelancers.send_proposal.recap.submitting_button",
                      )}
                    >
                      {t(
                        "company.search.freelancers.send_proposal.recap.submit_button",
                      )}
                    </Show>
                  </Button>
                </div>
              </div>
            </div>

            <Dialog open={dialogOpen()} onOpenChange={setDialogOpen}>
              <DialogContent class="bg-white">
                <DialogHeader>
                  <DialogTitle>
                    {t(
                      "company.search.freelancers.send_proposal.post_submit_dialog.title",
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    {t(
                      "company.search.freelancers.send_proposal.post_submit_dialog.description",
                    )}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter class="pt-2 ">
                  <Button variant="outline" onClick={handleContinueResearch}>
                    {t(
                      "company.search.freelancers.send_proposal.post_submit_dialog.continue_research",
                    )}
                  </Button>
                  <Button onClick={handleFinish}>
                    {t(
                      "company.search.freelancers.send_proposal.post_submit_dialog.finish",
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Show>
        </Container>
      </CompanyLayoutMainContentContainer>
    </>
  );
}
